import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Helper to get admin token
const getAdminToken = () => localStorage.getItem('adminToken');

// Admin axios instance with auth header
const adminAxios = axios.create({
  baseURL: API,
});

adminAxios.interceptors.request.use((config) => {
  const token = getAdminToken();
  if (token) {
    config.headers['x-admin-token'] = token;
  }
  return config;
});

// Fetch all categories
export const getCategories = async () => {
  try {
    const response = await axios.get(`${API}/categories`);
    return response.data;
  } catch (error) {
    console.error('Error fetching categories:', error);
    throw error;
  }
};

// Fetch single category
export const getCategory = async (id) => {
  try {
    const response = await axios.get(`${API}/categories/${id}`);
    return response.data;
  } catch (error) {
    console.error(`Error fetching category ${id}:`, error);
    throw error;
  }
};

// Submit inquiry
export const submitInquiry = async (inquiryData) => {
  try {
    const response = await axios.post(`${API}/inquiries`, inquiryData);
    return response.data;
  } catch (error) {
    console.error('Error submitting inquiry:', error);
    throw error;
  }
};

// Get all inquiries (Admin)
export const getInquiries = async () => {
  try {
    const response = await axios.get(`${API}/inquiries`);
    return response.data;
  } catch (error) {
    console.error('Error fetching inquiries:', error);
    throw error;
  }
};

// Admin API functions
export const adminLogin = async (password) => {
  try {
    const response = await axios.post(`${API}/admin/login`, { password });
    if (response.data.token) {
      localStorage.setItem('adminToken', response.data.token);
    }
    return response.data;
  } catch (error) {
    console.error('Admin login error:', error);
    throw error;
  }
};

export const adminLogout = async () => {
  try {
    await adminAxios.post('/admin/logout');
    localStorage.removeItem('adminToken');
  } catch (error) {
    localStorage.removeItem('adminToken');
  }
};

export const verifyAdminToken = async () => {
  try {
    const response = await adminAxios.get('/admin/verify');
    return response.data.valid;
  } catch (error) {
    localStorage.removeItem('adminToken');
    return false;
  }
};

export const getAdminStats = async () => {
  const response = await adminAxios.get('/admin/stats');
  return response.data;
};

export const getAdminCategories = async () => {
  const response = await adminAxios.get('/admin/categories');
  return response.data;
};

export const createCategory = async (data) => {
  const response = await adminAxios.post('/admin/categories', data);
  return response.data;
};

export const updateCategory = async (id, data) => {
  const response = await adminAxios.put(`/admin/categories/${id}`, data);
  return response.data;
};

export const deleteCategory = async (id) => {
  const response = await adminAxios.delete(`/admin/categories/${id}`);
  return response.data;
};

// Seed database with master product data
export const seedDatabase = async () => {
  const response = await adminAxios.post('/admin/seed-database');
  return response.data;
};

export const addBrand = async (categoryId, data) => {
  const response = await adminAxios.post(`/admin/categories/${categoryId}/brands`, data);
  return response.data;
};

export const updateBrand = async (categoryId, brandId, data) => {
  const response = await adminAxios.put(`/admin/categories/${categoryId}/brands/${brandId}`, data);
  return response.data;
};

export const deleteBrand = async (categoryId, brandId) => {
  const response = await adminAxios.delete(`/admin/categories/${categoryId}/brands/${brandId}`);
  return response.data;
};

export const addProduct = async (categoryId, brandId, data) => {
  const response = await adminAxios.post(`/admin/categories/${categoryId}/brands/${brandId}/products`, data);
  return response.data;
};

export const updateProduct = async (categoryId, brandId, productId, data) => {
  const response = await adminAxios.put(`/admin/categories/${categoryId}/brands/${brandId}/products/${productId}`, data);
  return response.data;
};

export const deleteProduct = async (categoryId, brandId, productId) => {
  const response = await adminAxios.delete(`/admin/categories/${categoryId}/brands/${brandId}/products/${productId}`);
  return response.data;
};

export const getAdminInquiries = async () => {
  const response = await adminAxios.get('/admin/inquiries');
  return response.data;
};

export const updateInquiryStatus = async (id, status) => {
  const response = await adminAxios.put(`/admin/inquiries/${id}/status`, { status });
  return response.data;
};

export const deleteInquiry = async (id) => {
  const response = await adminAxios.delete(`/admin/inquiries/${id}`);
  return response.data;
};


// Blog API functions (Public)
export const getBlogs = async () => {
  const response = await axios.get(`${API}/blogs`);
  return response.data;
};

export const getBlog = async (slug) => {
  const response = await axios.get(`${API}/blogs/${slug}`);
  return response.data;
};

// Blog API functions (Admin)
export const getAdminBlogs = async () => {
  const response = await adminAxios.get('/blogs/admin/all');
  return response.data;
};

export const createBlog = async (data) => {
  const response = await adminAxios.post('/blogs/admin/create', data);
  return response.data;
};

export const updateBlog = async (id, data) => {
  const response = await adminAxios.put(`/blogs/admin/${id}`, data);
  return response.data;
};

export const deleteBlog = async (id) => {
  const response = await adminAxios.delete(`/blogs/admin/${id}`);
  return response.data;
};

// File upload function
export const uploadFile = async (file) => {
  const formData = new FormData();
  formData.append('file', file);
  
  const response = await adminAxios.post('/upload', formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });
  
  // Return full URL
  return `${BACKEND_URL}${response.data.url}`;
};
